import { Heightfield } from '../terrain/heightfield'
import { Noise2D, hashString, mulberry32 } from '../core/rng'
import { DEG, bump, clamp, clamp01, lerp, smoothstep } from '../core/math'
import {
  cliffStep, couloirSlot, couloirWall, envelope, safeFade, spineRibs,
} from '../terrain/shapes'
import {
  countSinks, enforceDrainage, hydraulicErode, smooth, thermalErode,
} from '../terrain/generate/erosion'
import { paintRockAndSluff, paintSnowLayer } from '../terrain/generate/snowLayer'
import { computeHazards } from '../terrain/generate/hazards'
import { ObstacleField, scatterObstacles } from '../physics/obstacles'
import type { Venue } from '../terrain/generate/venues'
import type { SlopeDef } from './types'

/**
 * Grid resolution.
 *
 * Dropped from 2.5m to 1.8m for the realism pass — roughly twice the cells, so
 * erosion resolves finer drainage and the surface carries genuine relief rather
 * than relying entirely on shader detail. This is a *simulation* change, not
 * just a visual one: hazards, drainage and the physics all sample this grid, so
 * it moves par times and invalidates stored runs.
 */
const CELL = 1.8

export interface BuiltSlope {
  readonly def: SlopeDef
  readonly hf: Heightfield
  readonly obstacles: ObstacleField
  readonly start: { x: number; z: number }
  /** Split lines in metres, ascending, finish last. */
  readonly checkpointZ: number[]
  readonly finishZ: number
  readonly sinks: number
  readonly buildMs: number
}

/**
 * Build a designed slope. Deterministic from the definition alone: the PRNG is
 * seeded from the slope id and version, so surface grain and tree placement are
 * identical for every player and stay identical until the design is revised.
 *
 * Pass order matters, and one ordering decision is load-bearing:
 * `enforceDrainage` runs *after* the authored features are cut. Every feature
 * fades out somewhere, and a depression that closes up faster than the ground
 * falls away is an uphill ramp — a hole a racer falls into and cannot leave.
 * Filling depressions last makes that structurally impossible rather than
 * something the designer has to remember on every slope.
 */
export function buildSlope(def: SlopeDef): BuiltSlope {
  const t0 = now()
  const rng = mulberry32(hashString(`${def.id}/v${def.version}`))

  const hf = new Heightfield(-def.width / 2, 0, def.width, def.length, CELL)

  // --- Fall-line profile ---------------------------------------------------
  const profile = integrateProfile(def, hf)
  const meanAngle = meanProfileAngle(def)

  // --- Base surface --------------------------------------------------------
  const ridge = new Noise2D(rng)
  const lumps = new Noise2D(rng)
  const fine = new Noise2D(rng)
  const warp = new Noise2D(rng)
  const half = def.width / 2

  for (let iz = 0; iz < hf.rows; iz++) {
    const z = hf.zAt(iz)
    const tz = z / def.length
    for (let ix = 0; ix < hf.cols; ix++) {
      const x = hf.xAt(ix)
      let y = profile[iz]

      // Rim: rising ground on both flanks contains the course without an
      // invisible wall. Kept narrow — it is steep by nature, so a wide rim
      // dominates the slope statistics with ground nobody skis.
      const rimStrength = lerp(52, 20, smoothstep(0.5, 0.95, tz)) * def.rim
      const rimT = clamp01((Math.abs(x) / half - 0.8) / 0.2)
      y += rimStrength * rimT * rimT * 1.35

      // Domain warp so the grain meanders instead of running in stripes.
      const wx = x + warp.fbm(x * 0.0022, z * 0.0022, 2) * 32
      const wz = z + warp.fbm(x * 0.0022 + 40, z * 0.0022 - 25, 2) * 32

      // Amplitudes budgeted by wavelength: on a steep base, a metre of relief
      // at wavelength L adds about atan(2*PI*A/L) of local angle.
      const detail = lerp(1.0, 0.12, smoothstep(0.74, 0.96, tz))
      y += ridge.ridged(wx * 0.011, wz * 0.006, 4) * def.grain[0] * detail
      y += lumps.fbm(wx * 0.021, wz * 0.019, 4) * def.grain[1] * detail
      y += fine.fbm(x * 0.09, z * 0.085, 2) * def.grain[2] * detail

      hf.height[hf.index(ix, iz)] = y
    }
  }

  // --- Erosion -------------------------------------------------------------
  // Scale with cell count so erosion bites as hard per unit area as before.
  hydraulicErode(hf, rng, Math.round(def.erosionDroplets * 1.93))
  thermalErode(hf, def.repose, 60)
  smooth(hf, 0.35)

  // --- Authored features ---------------------------------------------------
  sculpt(def, hf, meanAngle, profile, rng)

  // Trim spikes left by carving, generously enough that cliff bands survive as
  // cliffs — running this near the snow's angle of repose quietly sands them
  // back into ordinary steep ground.
  //
  // +24 rather than +18. An authored band drops its full height over 7m, so on
  // a 47° pitch a 13m band stands at ~71° composite — which sat exactly on the
  // old cap. The face shed, thermal transport piled the material at its own
  // foot, and the resulting bench was flat enough to strand anyone who landed
  // on it. A cliff the designer asked for should not erode into a trap at the
  // bottom of itself; genuine carving spikes are far steeper than this and are
  // still caught.
  thermalErode(hf, def.repose + 24, 14)
  enforceDrainage(hf)
  const sinks = countSinks(hf)

  hf.computeConcavity()

  // --- Snow, rock, hazards -------------------------------------------------
  // The Phase 2 snow model runs unchanged; it just takes designed conditions.
  paintSnowLayer(hf, rng, asVenue(def), {
    windDir: def.conditions.windDir * DEG,
    windStrength: def.conditions.windStrength,
    tempC: def.conditions.tempC,
    daysSinceSnow: def.conditions.daysSinceSnow,
    newSnowCm: def.conditions.newSnowCm,
    sun: def.conditions.sun,
    sunAspect: def.conditions.sunAspect * DEG,
    summary: def.conditions.summary,
  })
  paintRockAndSluff(hf, rng)
  computeHazards(hf)

  const geo = corridorGeometry(def)
  // The gate is authored in the corridor's frame like everything else.
  const start = {
    x: def.start.x + (geo ? geo.centreAt(def.start.z / def.length) : 0),
    z: def.start.z,
  }
  const obstacles = scatterObstacles(hf, rng, {
    treeline: def.treeline,
    density: def.treeDensity,
    openRunoutZ: def.length * (def.finish - 0.12),
    ...(geo ? {
      corridorFloor: (x: number, z: number) => geo.floorAt(x, z),
      corridorBand: (z: number) => {
        const t = (z - hf.minZ) / hf.length
        return { centre: geo.centreAt(t), half: geo.halfAt(t) }
      },
    } : {}),
  })

  return {
    def,
    hf,
    obstacles,
    start,
    checkpointZ: def.checkpoints.map((c) => c * def.length),
    finishZ: def.finish * def.length,
    sinks,
    buildMs: now() - t0,
  }
}

// ---------------------------------------------------------------------------
// Profile
// ---------------------------------------------------------------------------

function angleAt(def: SlopeDef, t: number): number {
  const p = def.profile
  if (t <= p[0][0]) return p[0][1] * DEG
  for (let i = 1; i < p.length; i++) {
    if (t <= p[i][0]) {
      return lerp(p[i - 1][1], p[i][1], smoothstep(p[i - 1][0], p[i][0], t)) * DEG
    }
  }
  return p[p.length - 1][1] * DEG
}

function meanProfileAngle(def: SlopeDef): number {
  let sum = 0
  for (let i = 0; i <= 20; i++) sum += angleAt(def, i / 20) / DEG
  return sum / 21
}

/**
 * Integrate tan(angle) down the slope, then scale so the vertical lands exactly
 * on the authored figure. Scaling the tangents rather than the elevations keeps
 * the profile's shape and its relative steepness intact.
 */
function integrateProfile(def: SlopeDef, hf: Heightfield): Float32Array {
  const raw = new Float32Array(hf.rows)
  const SUB = 4
  const dz = CELL / SUB
  let drop = 0
  for (let iz = 1; iz < hf.rows; iz++) {
    const z0 = hf.zAt(iz - 1)
    for (let k = 0; k < SUB; k++) {
      const a = Math.tan(angleAt(def, (z0 + k * dz) / def.length))
      const b = Math.tan(angleAt(def, (z0 + (k + 1) * dz) / def.length))
      drop += ((a + b) * 0.5) * dz
    }
    raw[iz] = drop
  }
  const scale = drop > 1 ? def.vertical / drop : 1
  const top = def.vertical + 60
  const out = new Float32Array(hf.rows)
  for (let iz = 0; iz < hf.rows; iz++) out[iz] = top - raw[iz] * scale
  return out
}

// ---------------------------------------------------------------------------
// Authored features
// ---------------------------------------------------------------------------

/** Piecewise-linear track through [at, value] control points, smoothstepped. */
function trackAt(points: ReadonlyArray<readonly [number, number]>, t: number): number {
  if (t <= points[0][0]) return points[0][1]
  for (let i = 1; i < points.length; i++) {
    if (t <= points[i][0]) {
      return lerp(points[i - 1][1], points[i][1], smoothstep(points[i - 1][0], points[i][0], t))
    }
  }
  return points[points.length - 1][1]
}

export interface CorridorGeometry {
  /** Centreline x, metres, at a fraction of the slope's length. */
  centreAt(t: number): number
  /** Floor half-width, metres, at a fraction of the slope's length. */
  halfAt(t: number): number
  /** d(centre)/dz — positive when the run is bending toward +x. */
  driftAt(t: number): number
  /** 1 on the floor, falling to 0 just outside it. */
  floorAt(x: number, z: number): number
}

/**
 * Sampling helpers for a slope's corridor. Shared, because the tree scatter and
 * the audit tools have to agree with the sculptor about where the floor is —
 * two implementations of "where is the run" is one too many.
 */
export function corridorGeometry(def: SlopeDef): CorridorGeometry | null {
  const cor = def.corridor
  if (!cor) return null
  const L = def.length
  const centreAt = (t: number): number => trackAt(cor.path, t)
  const halfAt = (t: number): number => trackAt(cor.width, t)

  // --- Drift, heavily smoothed ---------------------------------------------
  // The raw derivative of the centreline reverses sign over a few metres where
  // one bend hands off to the next. Banking is that number times lateral
  // distance, so an abrupt reversal tips the whole cross-section through
  // horizontal and back — which builds a flat saddle at the foot of each wall,
  // and a flat saddle on powder is where a racer stops for good. Six of the
  // audit's thirty-three zero-input release points came to rest in one.
  //
  // Smoothing spreads each hand-off over ~a fifth of the run, which is also
  // what a banked turn is supposed to look like: the bank eases in before the
  // apex and eases out after it, rather than switching hands at the join.
  const N = 512
  let drift = new Float32Array(N + 1)
  for (let i = 0; i <= N; i++) {
    const a = Math.max(0, (i - 1) / N)
    const b = Math.min(1, (i + 1) / N)
    drift[i] = (centreAt(b) - centreAt(a)) / ((b - a) * L)
  }
  const radius = Math.max(1, Math.round(N * 0.045))
  for (let pass = 0; pass < 3; pass++) {
    const next = new Float32Array(N + 1)
    for (let i = 0; i <= N; i++) {
      let sum = 0
      for (let k = -radius; k <= radius; k++) sum += drift[clamp(i + k, 0, N)]
      next[i] = sum / (radius * 2 + 1)
    }
    drift = next
  }

  return {
    centreAt,
    halfAt,
    driftAt(t: number): number {
      const f = clamp01(t) * N
      const i = Math.min(N - 1, Math.floor(f))
      return lerp(drift[i], drift[i + 1], f - i)
    },
    floorAt(x: number, z: number): number {
      const t = z / L
      // Full on the floor, gone by the top of the wall — trees belong on the
      // shoulders, and a corridor this narrow has no room for a stray one.
      return 1 - smoothstep(1.05, 1.9, Math.abs(x - centreAt(t)) / halfAt(t))
    },
  }
}

/**
 * The corridor's contribution to every cell, as its own buffer.
 *
 * Kept separate because it has to be rate-limited down the fall line before it
 * is applied, and that is a sequential pass the per-cell sculpt loop can't do.
 *
 * The constraint: the outside wall of a bend sweeps sideways over fixed ground
 * as the centreline moves away from it, so a point out there climbs at roughly
 * `meander rate x wall gradient`. On The Gullet that is 0.70 x 1.17, or 39°,
 * against a 43° fall line — so the flanks rise almost as fast as the mountain
 * falls, and where surface grain tips the balance the ground goes genuinely
 * uphill. `enforceDrainage` then floods those hollows into flat ponds, and a
 * flat pond on powder is where a stopped racer stays.
 *
 * Rather than trade the meander against wall steepness by hand on every slope,
 * cap the growth: the corridor may never climb by more than a fraction of what
 * the fall line drops over the same distance. Where nothing is sweeping this
 * changes nothing; on the outside of a hard bend the wall quietly gets shorter,
 * which is what a drainage cutting into its own outer bank looks like anyway.
 */
const CORRIDOR_CLIMB = 0.55

function corridorLift(
  def: SlopeDef,
  hf: Heightfield,
  profile: Float32Array,
  rng: () => number,
): Float32Array | null {
  const cor = def.corridor
  const geo = corridorGeometry(def)
  if (!cor || !geo) return null

  // Flute wander, so the runnels are not a comb. Very low frequency down the
  // fall line: they have to stay near-vertical or they dam their own drainage.
  const wander = new Noise2D(rng)

  const lift = new Float32Array(hf.cols * hf.rows)
  for (let iz = 0; iz < hf.rows; iz++) {
    const z = hf.zAt(iz)
    const tz = z / def.length
    const cx = geo.centreAt(tz)
    const hw = geo.halfAt(tz)
    // Banking. The bend's outside lifts and its inside drops, so the floor
    // tilts into the turn: hold a high line and you carry speed, drop to the
    // inside and you take the shorter, slower, scrubbier route.
    const bank = -geo.driftAt(tz) * cor.banking
    for (let ix = 0; ix < hf.cols; ix++) {
      const x = hf.xAt(ix)
      // Distance outside the floor in *metres*, not half-widths: where the
      // corridor narrows, half-widths sweep the wall outward across fixed
      // ground for no reason other than the floor getting thinner.
      const edge = Math.abs(x - cx) - hw
      // Squared so the foot joins the floor tangentially rather than as a kerb;
      // past the wall the shoulders keep climbing more gently.
      const rise = clamp01(edge / cor.wallRun)

      // Sluff runnels, ~9m apart, cut into the wall face. Phase is driven by
      // world x so they run down the fall line; the wander term only bends them
      // enough to stop them reading as a comb.
      //
      // Two envelopes, both needed. They fade in from the foot of the wall
      // (nothing carves a runnel into flat ground) and *out* again above its
      // top — sluff runs on the face, not across the shoulder behind it, and
      // without the second envelope the `rise` term saturates and stripes the
      // entire mountain to the rim. Depth is modulated by noise as well, so
      // some runnels are deep, some are barely there, and none of them are
      // evenly spaced.
      const onFace = rise * (1 - smoothstep(1.0, 2.3, edge / cor.wallRun))
      const flute = edge > 0
        ? Math.cos(x * 0.7 + wander.fbm(x * 0.008, z * 0.0035, 2) * 3.4)
          * cor.fluting * onFace * (0.45 + 0.75 * clamp01(wander.fbm(x * 0.03 + 88, z * 0.006, 2) + 0.5))
        : 0

      lift[hf.index(ix, iz)] = cor.wallHeight * rise * rise
        + Math.max(0, edge - cor.wallRun) * 0.34
        + bank * clamp(x - cx, -hw * 1.2, hw * 1.2)
        - flute
    }
  }

  for (let iz = 1; iz < hf.rows; iz++) {
    const allowance = Math.max(0, profile[iz - 1] - profile[iz]) * CORRIDOR_CLIMB
    for (let ix = 0; ix < hf.cols; ix++) {
      const i = hf.index(ix, iz)
      const above = lift[i - hf.cols] + allowance
      if (lift[i] > above) lift[i] = above
    }
  }
  return lift
}

interface NaturalJump {
  z: number
  /** Lateral offset from the corridor centreline. */
  x: number
  height: number
  /** Length of the take-off ramp, metres. The lip is 0.6x this behind it. */
  run: number
  halfWidth: number
}

/**
 * Place the small lips. Deterministic from the slope seed, evenly spaced with
 * jitter so they neither march nor clump, and pushed clear of the cliff bands —
 * a jump that fires you off an 11m drop you had not chosen is not a jump, it is
 * an ambush.
 */
function planJumps(def: SlopeDef, geo: CorridorGeometry | null, rng: () => number): NaturalJump[] {
  const out: NaturalJump[] = []
  const n = def.naturalJumps
  if (n <= 0) return out
  const L = def.length

  for (let k = 0; k < n; k++) {
    // Size first: the clearance below depends on how far the ramp reaches.
    const run = 15 + rng() * 9
    const height = 1.5 + rng() * 2.3
    let z = L * (0.10 + (0.75 * (k + 0.5)) / n + (rng() - 0.5) * (0.55 / n))
    const x01 = rng()
    const w01 = rng()

    // Clear the *whole ramp* of every cliff band, not just the take-off.
    // Nudging by the jump's centre alone put a 40m ramp starting at the foot of
    // The Gullet's second band — which dammed the base of the cliff into a flat
    // bench, and three zero-input releases landed in it and stayed.
    const reach = run * 1.8
    for (const b of def.cliffBands) {
      const bz = b.at * L
      if (z - reach < bz + 55 && z > bz - 45) z = bz + 55 + reach
    }
    // Rather than squeeze a nudged jump into the runout, drop it.
    if (z > L * 0.88) continue

    const hw = geo ? geo.halfAt(z / L) : def.width * 0.25
    out.push({
      z,
      x: (x01 * 2 - 1) * hw * 0.5,
      height,
      run,
      halfWidth: hw * (0.30 + w01 * 0.28),
    })
  }
  return out
}

function sculpt(
  def: SlopeDef,
  hf: Heightfield,
  meanAngle: number,
  profile: Float32Array,
  rng: () => number,
): void {
  const L = def.length
  const half = def.width / 2
  const apron = new Noise2D(rng)
  const geo = corridorGeometry(def)
  const lift = corridorLift(def, hf, profile, rng)
  const jumps = planJumps(def, geo, rng)

  /** Corridor centreline at a downhill position; 0 on a slope with no corridor. */
  const axis = (z: number): number => (geo ? geo.centreAt(z / L) : 0)

  const couloirCentre = (c: SlopeDef['couloirs'][number], z: number): number =>
    axis(z) + lerp(c.xTop, c.xBottom, smoothstep(c.from * L, c.to * L, z))
  const couloirHalf = (c: SlopeDef['couloirs'][number], z: number): number => {
    const choke = c.chokeAt != null && c.chokeAmount
      ? c.chokeAmount * bump((z - c.chokeAt * L) / 55)
      : 0
    return Math.max(3.5, c.halfWidth - choke)
  }

  for (let iz = 0; iz < hf.rows; iz++) {
    const z = hf.zAt(iz)
    const tz = z / L
    for (let ix = 0; ix < hf.cols; ix++) {
      const x = hf.xAt(ix)
      const i = hf.index(ix, iz)
      let y = hf.height[i]

      // The corridor first, so every authored feature below layers on top of
      // it. Note it *raises the flanks* rather than trenching the floor: the
      // floor stays exactly on the authored fall-line profile, so the slope's
      // `vertical` still describes the run you actually ski.
      if (lift) y += lift[i]

      for (const c of def.couloirs) {
        const env = envelope(z, c.from * L, c.to * L, 55, safeFade(c.depth, meanAngle))
        if (env <= 0.001) continue
        const d = Math.abs(x - couloirCentre(c, z)) / couloirHalf(c, z)
        y -= c.depth * env * couloirSlot(d)
        y += c.depth * 0.32 * env * couloirWall(d)
      }

      for (const s of def.spines) {
        const env = envelope(z, s.from * L, s.to * L, 60, safeFade(s.amplitude, meanAngle))
        if (env <= 0.001) continue
        y += s.amplitude * env * spineRibs((x - axis(z) - s.centreX) / s.span, s.ribs)
      }

      for (const b of def.cliffBands) {
        let weakness = 0
        for (const g of b.gates) {
          weakness = Math.max(weakness, bump((x - axis(z) - g.x) / g.halfWidth) * g.ease)
        }
        // Taper into the rim so a band never slices the containing walls.
        const extent = smoothstep(-half, -half * 0.88, x) * (1 - smoothstep(half * 0.88, half, x))
        if (extent > 0.01) y -= cliffStep(z, b.at * L, b.drop * extent, weakness)
      }

      for (const r of def.rollovers) {
        const env = bump((z - r.at * L) / 78)
          * (1 - smoothstep(r.width * 0.55, r.width, Math.abs(x - axis(z) - r.x)))
        y += r.height * env
      }

      for (const d of def.drainages) {
        const env = envelope(z, d.from * L, d.to * L, 70, safeFade(d.depth, meanAngle))
        if (env > 0.001) y -= d.depth * env * bump((x - axis(z) - d.x) / d.width)
      }

      // Natural jumps. Asymmetric on purpose: a long ramp you can see coming
      // and a short lip that drops away, which is what makes a lump a take-off
      // rather than a bump. The ramp climbs ~3m over 25m, so at any slope angle
      // on this roster the ground is still falling underneath it.
      for (const j of jumps) {
        const u = (z - j.z) / j.run
        if (u < -1.8 || u > 0.7) continue
        const along = smoothstep(-1.7, 0, u) - smoothstep(0, 0.6, u)
        if (along <= 0.001) continue
        const across = 1 - smoothstep(0.5, 1.0, Math.abs(x - axis(z) - j.x) / j.halfWidth)
        y += j.height * along * across
      }

      // A shelf to stand on at the gate, so you start at rest rather than
      // already falling.
      const ledge = bump((z - def.start.z) / 34) * bump((x - axis(z) - def.start.x) / 50)
      if (ledge > 0.001) y = lerp(y, y + 2.2, ledge * 0.7)

      if (tz > 0.85) y += apron.fbm(x * 0.03, z * 0.03, 2) * 0.35

      hf.height[i] = y
    }
  }
}

/** The snow model takes a Venue for its bias; a designed slope supplies a flat one. */
function asVenue(def: SlopeDef): Venue {
  const r = (min: number, max: number) => ({ min, max })
  return {
    key: def.id, name: def.name, blurb: def.blurb,
    width: r(def.width, def.width), length: r(def.length, def.length),
    vertical: r(def.vertical, def.vertical),
    meanAngle: r(30, 30), angleVariance: r(0, 0),
    couloirs: r(0, 0), couloirWidth: r(0, 0), spineWall: r(0, 0),
    cliffBands: r(0, 0), rollovers: r(0, 0),
    treeline: r(def.treeline, def.treeline), treeDensity: r(def.treeDensity, def.treeDensity),
    erosion: r(0, 0), repose: r(def.repose, def.repose),
    snowBias: [0, 0, 0, 0],
  }
}

function now(): number {
  return typeof performance !== 'undefined' ? performance.now() : Date.now()
}

export { CELL as SLOPE_CELL }
export { clamp }
